import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, Check, Ban, ExternalLink } from 'lucide-react';
import { getAuthHeaders } from '../../utils/authHeaders';
import { AssetThumb } from '../shared-assets/AssetThumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Pick an operator logo from the shared assets library (telecom logos first, then everything). */
export function OperatorLogoPicker({ value, onChange, t }) {
  const [assets, setAssets] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = showAll ? {} : { category: 'telecom_logos' };
        const r = await axios.get(`${API}/admin/shared-assets`, { params, headers: getAuthHeaders() });
        let list = r.data?.data || [];
        if (!showAll && list.length === 0) {
          const all = await axios.get(`${API}/admin/shared-assets`, { headers: getAuthHeaders() });
          list = all.data?.data || [];
          if (alive) setShowAll(true);
        }
        if (alive) setAssets(list);
      } catch { if (alive) setAssets([]); }
    })();
    return () => { alive = false; };
  }, [showAll]);

  return (
    <div data-testid="operator-logo-picker">
      <div className="flex items-center justify-between">
        <label className="text-xs text-white/60">{t('شعار المشغّل (اختياري) — من قاعدة صور المنصات', 'Operator logo (optional) — from the shared image library')}</label>
        <div className="flex items-center gap-2 text-[10px]">
          <button type="button" onClick={() => setShowAll((v) => !v)} className="text-white/50 hover:text-white" data-testid="operator-logo-toggle-all">
            {showAll ? t('شعارات الاتصالات فقط', 'Telecom logos only') : t('كل الصور', 'All images')}
          </button>
          <Link to="/control/shared-assets" target="_blank" className="text-[#d4af37] hover:underline flex items-center gap-0.5" data-testid="operator-logo-library-link">
            <ExternalLink className="w-3 h-3" />{t('رفع شعار جديد', 'Upload new logo')}
          </Link>
        </div>
      </div>
      {assets === null ? (
        <div className="py-4 flex justify-center text-white/40"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : (
        <div className="mt-2 grid grid-cols-5 sm:grid-cols-6 gap-2 max-h-40 overflow-y-auto pr-1">
          <button type="button" onClick={() => onChange(null)} data-testid="operator-logo-none"
                  className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-[9px] ${!value ? 'border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]' : 'border-white/10 text-white/40 hover:border-white/30'}`}>
            <Ban className="w-4 h-4 mb-0.5" />{t('بدون', 'None')}
          </button>
          {assets.map((a) => (
            <button key={a.id} type="button" onClick={() => onChange(a.id)} title={`${a.name_ar} · ${a.id}`} data-testid={`operator-logo-${a.id}`}
                    className={`relative aspect-square rounded-lg border p-0.5 ${value === a.id ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-white/10 hover:border-white/30'}`}>
              <AssetThumb assetId={a.id} version={a.updated_at} className="w-full h-full border-0 bg-transparent" alt={a.name_ar} />
              {value === a.id && <Check className="w-3.5 h-3.5 text-[#d4af37] absolute top-0.5 end-0.5" />}
            </button>
          ))}
          {assets.length === 0 && (
            <div className="col-span-full text-[11px] text-white/40 py-3 text-center">
              {t('لا توجد صور في المكتبة بعد — ارفع شعارات المشغّلين أولاً من قاعدة صور المنصات', 'No images yet — upload operator logos in the shared library first')}
            </div>
          )}
        </div>
      )}
      {value && <div className="text-[10px] text-white/40 mt-1 font-mono" dir="ltr">logo_asset_id: {value}</div>}
    </div>
  );
}
